import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ServerDiscovery from '../services/ServerDiscovery';
import {
  applyAuthPayload,
  startGoogleSignIn,
} from '../utils/auth';
import { getPlayerId } from '../utils/playerId';
import { trackEvent } from '../utils/analytics';
import { TERMS_VERSION } from '../legal/terms';

function GoogleMark() {
  return (
    <svg className="lng-google-mark" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A8.99 8.99 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.69 9c0-.59.1-1.17.26-1.71V4.96H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function errorMessage(err, fallback) {
  const detail = err?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

/**
 * Sign-in panel: Google + email/password (register / login / forgot).
 * On success, applies the session and optionally navigates via `onSuccess`.
 */
function AuthPanel({ nextPath = '/langle', onSuccess, initialMode = 'login', onModeChange }) {
  const navigate = useNavigate();
  const [mode, setModeState] = useState(initialMode); // login | register | forgot
  const setMode = (next) => {
    setModeState(next);
    if (onModeChange) onModeChange(next);
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Explicit Terms acceptance. Required to create an account (email or
  // Google — Google may create one). Existing users signing in with email
  // don't need it here; TermsGate asks them if their signed version is old.
  const [agreed, setAgreed] = useState(false);
  // The agreement box only belongs on account creation. In sign-in mode a
  // Google sign-in may still create a new account; that account arrives
  // without a signed version and TermsGate asks for it immediately.
  const needsAgreement = mode === 'register';

  const finish = (data) => {
    applyAuthPayload(data);
    trackEvent(mode === 'register' ? 'email_sign_up' : 'email_sign_in');
    if (onSuccess) onSuccess(data);
    else if (nextPath) navigate(nextPath, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const base = await ServerDiscovery.discoverHttpBase();
      if (mode === 'forgot') {
        const res = await fetch(`${base}/auth/forgot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(errorMessage(err, 'Could not send reset email'));
        }
        setInfo('If that email has an account, we sent a reset link. Check your inbox.');
        return;
      }

      const path = mode === 'register' ? '/auth/register' : '/auth/login';
      const body = {
        email: email.trim(),
        password,
        playerId: getPlayerId(),
      };
      if (mode === 'register') {
        if (!agreed) throw new Error('Please accept the Terms of Service to create an account.');
        body.termsVersion = TERMS_VERSION;
        if (name.trim()) body.name = name.trim();
      }

      const res = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(errorMessage(err, mode === 'register' ? 'Could not create account' : 'Sign-in failed'));
      }
      const data = await res.json();
      finish(data);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lng-auth-panel">
      <button
        type="button"
        className="lng-google-btn"
        onClick={() => startGoogleSignIn(nextPath, needsAgreement ? TERMS_VERSION : null)}
      >
        <GoogleMark />
        {needsAgreement ? 'Sign up with Google' : 'Continue with Google'}
      </button>
      {needsAgreement && (
        // Sign-in wrap for Google: the notice sits directly under the button
        // it applies to, and the accepted version is sent with the code
        // exchange so the acceptance is recorded like the checkbox one.
        <p className="lng-terms-notice">
          By continuing with Google, you agree to our{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
        </p>
      )}

      <div className="lng-auth-or" aria-hidden>or</div>

      <form className="lng-auth-form" onSubmit={onSubmit}>
        {mode === 'register' && (
          <input
            className="lng-input"
            type="text"
            name="name"
            autoComplete="name"
            placeholder="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            disabled={busy}
          />
        )}
        <input
          className="lng-input"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={busy}
        />
        {mode !== 'forgot' && (
          <input
            className="lng-input"
            type="password"
            name="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            placeholder={mode === 'register' ? 'Password (8+ characters)' : 'Password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            disabled={busy}
          />
        )}
        {needsAgreement && (
          <label className="lng-terms-row">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={busy}
            />
            <span>
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
            </span>
          </label>
        )}
        <button
          type="submit"
          className="lng-google-btn lng-email-btn"
          disabled={busy || !email.trim() || (mode !== 'forgot' && password.length < 8) || (needsAgreement && !agreed)}
        >
          {busy
            ? 'Working…'
            : mode === 'register'
              ? 'Create account'
              : mode === 'forgot'
                ? 'Send reset link'
                : 'Sign in with email'}
        </button>
      </form>

      {error && <p className="lng-inline-error">{error}</p>}
      {info && <p className="lng-verify-hint">{info}</p>}

      <p className="lng-auth-switch">
        {mode === 'login' && (
          <>
            <button type="button" className="lng-text-btn" onClick={() => { setMode('register'); setError(''); setInfo(''); }}>
              Create an account
            </button>
            {' · '}
            <button type="button" className="lng-text-btn" onClick={() => { setMode('forgot'); setError(''); setInfo(''); setPassword(''); }}>
              Forgot password?
            </button>
          </>
        )}
        {mode === 'register' && (
          <>
            Already have an account?{' '}
            <button type="button" className="lng-text-btn" onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
              Sign in
            </button>
          </>
        )}
        {mode === 'forgot' && (
          <>
            Remembered it?{' '}
            <button type="button" className="lng-text-btn" onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
              Sign in
            </button>
            {' · '}
            <Link to="/account">Account</Link>
          </>
        )}
      </p>
    </div>
  );
}

export default AuthPanel;
